import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  PAYMENT_RAILS,
  type InvoiceDto,
  type PaymentDto,
  type PaymentRail,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';

// Operator-side invoice detail. Read-only summary plus the Finance actions:
//   - mark-paid (manual reconciliation, bank or card)
//   - void     (cancel an issued invoice)
//
// PATCH editing of issued invoices is supported by the API but kept off the
// UI for v1 — most adjustments should issue a corrected invoice + void the
// original for a clean audit trail.

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft',
  issued: 'Issued — awaiting payment',
  awaiting_wire: 'Bank wire initiated',
  received: 'Funds received — pending reconciliation',
  cleared: 'Cleared',
  void: 'Voided',
  refunded: 'Refunded',
};

function formatMoney(amount_pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(amount_pence / 100);
  } catch {
    return `${currency} ${(amount_pence / 100).toLocaleString()}`;
  }
}

function formatTs(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

interface PayForm {
  rail: PaymentRail;
  receivedAmount: string;
  receivedAt: string;
  externalRef: string;
  note: string;
}

function initialPayForm(inv: InvoiceDto): PayForm {
  return {
    rail: 'bank',
    receivedAmount: (inv.amount_pence / 100).toString(),
    receivedAt: new Date().toISOString().slice(0, 10),
    externalRef: '',
    note: '',
  };
}

export function AdminInvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const [inv, setInv] = useState<InvoiceDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'pay' | 'void' | 'refund'>(null);
  const [paying, setPaying] = useState(false);
  const [pay, setPay] = useState<PayForm | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  useEffect(() => {
    if (!id) return;
    apiGet<{ invoice: InvoiceDto }>(`/api/invoices/${id}`)
      .then((r) => { setInv(r.invoice); setPay(initialPayForm(r.invoice)); })
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : 'Could not load invoice.'));
  }, [id]);

  if (error && !inv) {
    return (
      <>
        <h1 className="portal-h1">Invoice not found</h1>
        <p className="portal-sub">{error}</p>
        <Link className="btn ghost" to="/admin/finance" style={{ marginTop: 24 }}>← Finance</Link>
      </>
    );
  }
  if (!inv || !pay) return <p className="portal-sub">Loading…</p>;

  const canPay = inv.status === 'issued' || inv.status === 'awaiting_wire' || inv.status === 'received';
  const canVoid = inv.status === 'issued' || inv.status === 'awaiting_wire';
  const refundCap = (inv.received_amount_pence ?? inv.amount_pence) ?? 0;
  const alreadyRefunded = inv.refunded_amount_pence ?? 0;
  const refundRemaining = refundCap - alreadyRefunded;
  const canRefund = inv.status === 'cleared' && refundRemaining > 0;

  async function handlePay() {
    if (!inv || busy) return;
    const amount = Number(pay!.receivedAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Received amount must be a positive number.');
      return;
    }
    setBusy('pay'); setError(null);
    try {
      const r = await apiPost<{ invoice: InvoiceDto }>(`/api/invoices/${inv.id}/mark-paid`, {
        rail: pay!.rail,
        received_amount_pence: Math.round(amount * 100),
        received_at: pay!.receivedAt ? new Date(pay!.receivedAt).toISOString() : undefined,
        external_ref: pay!.externalRef.trim() || undefined,
        note: pay!.note.trim() || undefined,
      });
      setInv(r.invoice);
      setPaying(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Mark-paid failed.');
    } finally {
      setBusy(null);
    }
  }

  function openRefund() {
    if (!inv) return;
    setRefundAmount((refundRemaining / 100).toString());
    setRefundReason('');
    setRefundOpen(true);
    setError(null);
  }

  async function handleRefund() {
    if (!inv || busy) return;
    const amount = Number(refundAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Refund amount must be a positive number.');
      return;
    }
    const minor = Math.round(amount * 100);
    if (minor > refundRemaining) {
      setError(`Refund exceeds remaining refundable balance (${formatMoney(refundRemaining, inv.currency)}).`);
      return;
    }
    const reason = refundReason.trim();
    if (reason.length === 0) {
      setError('Refund reason is required.');
      return;
    }
    if (reason.length > 500) {
      setError('Refund reason must be 500 characters or fewer.');
      return;
    }
    setBusy('refund'); setError(null);
    try {
      const r = await apiPost<{ invoice: InvoiceDto; refund: PaymentDto }>(
        `/api/invoices/${inv.id}/refund`,
        { amount_minor: minor === refundRemaining ? undefined : minor, reason },
      );
      setInv(r.invoice);
      setRefundOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Refund failed.');
    } finally {
      setBusy(null);
    }
  }

  async function handleVoid() {
    if (!inv || busy) return;
    if (!window.confirm(`Void invoice ${inv.id}? This is auditable but cannot be undone.`)) return;
    setBusy('void'); setError(null);
    try {
      const r = await apiPost<{ invoice: InvoiceDto }>(`/api/invoices/${inv.id}/void`, {});
      setInv(r.invoice);
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'Void failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--fg-mute)', marginBottom: 8 }}>
        <Link to="/admin/finance" style={{ borderBottom: '1px solid var(--rule)' }}>Finance</Link>
        {' / '}<span className="rid">{inv.id}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 className="portal-h1">Invoice {inv.id}</h1>
          <p className="portal-sub">
            Proposal{' '}
            <Link to={`/admin/proposals/${inv.proposal_id}`} className="rid" style={{ borderBottom: '1px solid var(--rule)' }}>
              {inv.proposal_id}
            </Link>
            {' · '}
            <span>{inv.client_name ?? inv.client_id}</span>
          </p>
        </div>
      </div>

      <p className="portal-sub" style={{ marginTop: 14 }}>{STATUS_LABEL[inv.status]}</p>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="proposal-form">
        <div className="card">
          <h3 className="proposal-section-h">Summary</h3>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, letterSpacing: '-0.02em', marginBottom: 6, fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(inv.amount_pence, inv.currency)}
          </div>
          <div className="proposal-kv">
            <span>Reference</span>
            <strong style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{inv.payment_reference}</strong>
          </div>
          <div className="proposal-kv">
            <span>Issued</span>
            <strong>{inv.issued_at?.slice(0, 10) ?? '—'}</strong>
          </div>
          <div className="proposal-kv">
            <span>Due</span>
            <strong>{inv.due_at.slice(0, 10)}</strong>
          </div>
          {inv.milestone_label && (
            <div className="proposal-kv">
              <span>Milestone</span>
              <strong>{inv.milestone_label}</strong>
            </div>
          )}
          {inv.paid_at && (
            <div className="proposal-kv">
              <span>Paid</span>
              <strong>{inv.paid_at.slice(0, 10)}</strong>
            </div>
          )}
          {inv.received_amount_pence != null && (
            <div className="proposal-kv">
              <span>Received</span>
              <strong>{formatMoney(inv.received_amount_pence, inv.currency)}</strong>
            </div>
          )}
          {alreadyRefunded > 0 && (
            <div className="proposal-kv">
              <span>Refunded</span>
              <strong>{formatMoney(alreadyRefunded, inv.currency)}</strong>
            </div>
          )}
          {inv.external_ref && (
            <div className="proposal-kv">
              <span>External ref</span>
              <strong style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{inv.external_ref}</strong>
            </div>
          )}
          {inv.notes && (
            <>
              <h4 className="proposal-sub-h">Notes</h4>
              <p className="proposal-prose">{inv.notes}</p>
            </>
          )}
        </div>

        {inv.payments && inv.payments.length > 0 && (
          <div className="card">
            <h3 className="proposal-section-h">Payments</h3>
            <AdminPaymentsTimeline payments={inv.payments} currency={inv.currency} />
          </div>
        )}

        {(canPay || canVoid || canRefund) && (
          <div className="card">
            <h3 className="proposal-section-h">Finance actions</h3>
            {canPay && !paying && (
              <button type="button" className="btn" onClick={() => setPaying(true)} disabled={busy !== null} style={{ marginBottom: 10 }}>
                Mark as paid
              </button>
            )}

            {canPay && paying && (
              <>
                <label className="prop-lbl">Payment rail</label>
                <div className="chip-row" style={{ marginBottom: 12 }}>
                  {PAYMENT_RAILS.map((r) => (
                    <button
                      key={r}
                      type="button"
                      className={`chip-btn ${pay.rail === r ? 'on' : ''}`}
                      onClick={() => setPay((p) => p ? { ...p, rail: r } : p)}
                      disabled={busy !== null}
                    >
                      {r === 'bank' ? 'Bank transfer' : 'Card (Revolut)'}
                    </button>
                  ))}
                </div>

                <label className="prop-lbl" htmlFor="amt">Amount received</label>
                <input
                  id="amt"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  className="prop-input"
                  value={pay.receivedAmount}
                  disabled={busy !== null}
                  onChange={(e) => setPay((p) => p ? { ...p, receivedAmount: e.target.value } : p)}
                />

                <label className="prop-lbl" htmlFor="rdate" style={{ marginTop: 12 }}>Received on</label>
                <input
                  id="rdate"
                  type="date"
                  className="prop-input"
                  value={pay.receivedAt}
                  disabled={busy !== null}
                  onChange={(e) => setPay((p) => p ? { ...p, receivedAt: e.target.value } : p)}
                />

                <label className="prop-lbl" htmlFor="exref" style={{ marginTop: 12 }}>External reference</label>
                <input
                  id="exref"
                  className="prop-input"
                  placeholder="bank ref / Revolut payment id"
                  value={pay.externalRef}
                  disabled={busy !== null}
                  onChange={(e) => setPay((p) => p ? { ...p, externalRef: e.target.value } : p)}
                />

                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button type="button" className="btn" onClick={handlePay} disabled={busy !== null}>
                    {busy === 'pay' ? 'Saving…' : 'Confirm payment'}
                  </button>
                  <button type="button" className="btn ghost" onClick={() => setPaying(false)} disabled={busy !== null}>
                    Cancel
                  </button>
                </div>
              </>
            )}

            {canVoid && !paying && (
              <button type="button" className="btn ghost" onClick={handleVoid} disabled={busy !== null} style={{ marginTop: 10 }}>
                {busy === 'void' ? 'Voiding…' : 'Void invoice'}
              </button>
            )}

            {canRefund && !paying && !refundOpen && (
              <button type="button" className="btn ghost" onClick={openRefund} disabled={busy !== null} style={{ marginTop: 10 }}>
                Issue refund
              </button>
            )}

            {canRefund && refundOpen && (
              <div style={{ marginTop: 14, borderTop: '1px solid var(--rule, #e7e5e4)', paddingTop: 14 }}>
                <h4 className="proposal-sub-h">Issue refund</h4>
                <p style={{ fontSize: 13, color: 'var(--fg-soft)', marginBottom: 10 }}>
                  Remaining refundable balance: <strong>{formatMoney(refundRemaining, inv.currency)}</strong>
                </p>

                <label className="prop-lbl" htmlFor="rfamt">Refund amount</label>
                <input
                  id="rfamt"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  className="prop-input"
                  value={refundAmount}
                  disabled={busy !== null}
                  onChange={(e) => setRefundAmount(e.target.value)}
                />

                <label className="prop-lbl" htmlFor="rfreason" style={{ marginTop: 12 }}>Reason</label>
                <textarea
                  id="rfreason"
                  className="prop-input"
                  rows={3}
                  maxLength={500}
                  placeholder="Why is this refund being issued?"
                  value={refundReason}
                  disabled={busy !== null}
                  onChange={(e) => setRefundReason(e.target.value)}
                />
                <div style={{ fontSize: 11, color: 'var(--fg-mute)', textAlign: 'right' }}>
                  {refundReason.length}/500
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button type="button" className="btn" onClick={handleRefund} disabled={busy !== null}>
                    {busy === 'refund' ? 'Refunding…' : 'Confirm refund'}
                  </button>
                  <button type="button" className="btn ghost" onClick={() => setRefundOpen(false)} disabled={busy !== null}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="proposal-actions">
        <Link to="/admin/finance" className="btn ghost">← Back to finance</Link>
      </div>
    </>
  );
}

function AdminPaymentsTimeline({ payments, currency }: { payments: PaymentDto[]; currency: string }) {
  const rows = [...payments].sort((a, b) => a.created_at.localeCompare(b.created_at));
  return (
    <table className="payments-timeline" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--fg-mute)', fontWeight: 500 }}>
          <th style={{ padding: '6px 8px' }}>Ref</th>
          <th style={{ padding: '6px 8px' }}>When</th>
          <th style={{ padding: '6px 8px' }}>Method</th>
          <th style={{ padding: '6px 8px' }}>Status</th>
          <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount</th>
          <th style={{ padding: '6px 8px' }}>External</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => (
          <AdminPaymentRow key={p.id} p={p} currency={currency} />
        ))}
      </tbody>
    </table>
  );
}

function AdminPaymentRow({ p, currency }: { p: PaymentDto; currency: string }) {
  const methodLabel =
    p.method === 'card'
      ? `Card · Revolut${p.card_network ? ` · ${p.card_network.toUpperCase()}` : ''}${p.card_last4 ? ` ····${p.card_last4}` : ''}`
      : 'Wire transfer';
  const statusLabel: Record<PaymentDto['status'], string> = {
    pending: 'Pending',
    succeeded: 'Succeeded',
    failed: 'Failed',
    refunded: 'Refunded',
  };
  const isRefunded = p.status === 'refunded';
  const isFailed = p.status === 'failed';
  const externalRef = p.external_ref ?? null;
  return (
    <>
      <tr style={{ borderTop: '1px solid var(--rule, #e7e5e4)' }}>
        <td style={{ padding: '8px', fontFamily: 'var(--mono)', fontSize: 12 }}>{p.id}</td>
        <td style={{ padding: '8px', color: 'var(--fg-soft)' }}>{formatTs(p.created_at)}</td>
        <td style={{ padding: '8px' }}>{methodLabel}</td>
        <td style={{ padding: '8px' }}>
          <AdminStatusPill status={p.status}>{statusLabel[p.status]}</AdminStatusPill>
        </td>
        <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {isRefunded ? `(${formatMoney(p.refund_amount_minor ?? p.amount_minor, currency)})` : formatMoney(p.amount_minor, currency)}
        </td>
        <td style={{ padding: '8px' }}>
          {externalRef && (
            <CopyChip value={externalRef} />
          )}
        </td>
      </tr>
      {isFailed && p.failure_message && (
        <tr>
          <td colSpan={6} style={{ padding: '0 8px 8px 8px', fontSize: 12, color: '#991b1b' }}>
            {p.failure_code ? `${p.failure_code}: ` : ''}{p.failure_message}
          </td>
        </tr>
      )}
      {isRefunded && p.refund_reason && (
        <tr>
          <td colSpan={6} style={{ padding: '0 8px 8px 8px', fontSize: 12, color: 'var(--fg-mute)' }}>
            Refund reason: {p.refund_reason}
          </td>
        </tr>
      )}
    </>
  );
}

function CopyChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        try {
          void navigator.clipboard?.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* ignore */
        }
      }}
      style={{
        fontFamily: 'var(--mono)',
        fontSize: 11,
        padding: '2px 6px',
        border: '1px solid var(--rule, #e7e5e4)',
        background: 'transparent',
        borderRadius: 4,
        cursor: 'pointer',
        color: copied ? 'var(--accent, #2b9bc3)' : 'inherit',
      }}
      title={copied ? 'Copied' : 'Click to copy'}
    >
      {copied ? '✓ copied' : value.length > 18 ? value.slice(0, 16) + '…' : value}
    </button>
  );
}

function AdminStatusPill({ status, children }: { status: PaymentDto['status']; children: React.ReactNode }) {
  const palette: Record<PaymentDto['status'], { bg: string; fg: string }> = {
    pending: { bg: 'rgba(217,119,6,0.10)', fg: '#92400e' },
    succeeded: { bg: 'rgba(22,163,74,0.12)', fg: '#166534' },
    failed: { bg: 'rgba(185,28,28,0.10)', fg: '#991b1b' },
    refunded: { bg: 'var(--bg-soft, #f5f5f4)', fg: 'var(--fg, #1c1917)' },
  };
  const c = palette[status];
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      background: c.bg,
      color: c.fg,
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 500,
    }}>
      {children}
    </span>
  );
}
